import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { PyrenzBlueButton } from '@client/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useUserStore } from '@client/store';
import { useState, useEffect } from 'react';
import { AuthManager } from '@client/utility';

type TopLoginSectionProps = {
  setShowLogin: (val: boolean) => void;
  setShowRegister: (val: boolean) => void;
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.3 } },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6 } },
};

export function TopLoginSection({
  setShowLogin,
  setShowRegister,
}: TopLoginSectionProps) {
  const is_login = useUserStore((state) => state.is_login);
  const [isChecking, setIsChecking] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const loggedIn = await AuthManager.checkLogin();
      setIsLoggedIn(loggedIn);
      setIsChecking(false);
    };
    checkAuth();
  }, [is_login]);

  if (isChecking || isLoggedIn || is_login) return null;

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="show"
      sx={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        backgroundImage: 'url(/LogInBg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        textAlign: 'center',
        px: 2,
        py: 4,
        overflow: 'hidden',
      }}
    >
      <Box component={motion.div} variants={itemVariants} sx={{ mb: 6, px: 2 }}>
        <motion.div variants={itemVariants}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: 28, md: 40 },
              color: 'rgba(255,255,255,0.95)',
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}
          >
            Ignite your imagination.
          </Typography>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: 28, md: 40 },
              color: 'rgba(255,255,255,0.95)',
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}
          >
            Every flame writes a new beginning, shaping worlds untold.
          </Typography>
        </motion.div>
      </Box>

      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          mb: 4,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <PyrenzBlueButton
          onClick={() => setShowLogin(true)}
          sx={{ minWidth: 160 }}
        >
          Login
        </PyrenzBlueButton>

        <PyrenzBlueButton
          onClick={() => setShowRegister(true)}
          sx={{
            minWidth: 160,
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' },
          }}
        >
          Register
        </PyrenzBlueButton>
      </Box>

      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          opacity: 0.9,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 500 }}>
          Scroll Down
        </Typography>
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ExpandMoreIcon fontSize="large" />
        </motion.div>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}